import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, SelectPicker, IconButton, Checkbox, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { getPagedUsers } from '@/services/user.service';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { User, UserGenderList, UserPaginationFilterRequest, UserRolesList } from '@/models/user.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const UserList = () => {
  const [filterReq, setFilterReq] = useState<UserPaginationFilterRequest>(new UserPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  
  const nav = useNavigate();
  
  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: UserPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<User> = await getPagedUsers(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching users:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/users/new`)}>
          Add User
        </Button>
        <Stack spacing={6}>
          <SelectPicker
            label="Role"
            data={UserRolesList}
            searchable={false}
            value={filterReq.role ?? null}
            onChange={dataKey => setFilterReq(pre => ({...pre, role: dataKey ?? undefined}))}
          />
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          /> 
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Profile</HeaderCell>
          <ImageCell dataKey="profilePicture" />
        </Column>    
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Username</HeaderCell>
          <Cell dataKey="username" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>First Name</HeaderCell>
          <Cell dataKey="firstName" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Last Name</HeaderCell>
          <Cell dataKey="lastName" />
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Gender</HeaderCell>          
          <Cell dataKey="gender">
            {(rowData: User) => UserGenderList.findLast( g => g.value == rowData.gender )?.label ?? 'N/A' }                
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Role</HeaderCell>          
          <Cell dataKey="role">
            {(rowData: User) => UserRolesList.findLast( r => r.value == rowData.role)?.label ?? 'N/A'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Is Email Verified</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: User) => (   
            <Checkbox checked={rowData.isEmailVerified} readOnly></Checkbox>
          )}
          </Cell>
        </Column>  
        <Column flexGrow={1} fixed="right">
        <HeaderCell>...</HeaderCell>
        <Cell style={{ padding: '6px' }}>
          {rowData => <IconButton onClick={() => nav(`/users/${rowData.id}`)} icon={<EditIcon />} circle size="sm" /> }
        </Cell>
      </Column>
        
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
      
    </PageContent>
  );
};

export default UserList;
